/**
 * Commerce · Shopify Sync placeholder.
 */
import { useState } from "react";
import { RefreshCcw, CheckCircle2, AlertOctagon, PlugZap, KeyRound } from "lucide-react";
import { Card, SectionHeader, Button, TextInput } from "../../components/lib";
import { Field, Toggle } from "../../components/lib.overlay";
import { SYNC_STREAMS, SyncStream } from "../../mocks/commerceData";

const STATUS_META: Record<SyncStream["status"], { icon: any; label: string; tint: string; fg: string }> = {
  "ok":            { icon: CheckCircle2, label: "In sync",       tint: "var(--ph-success-tint)", fg: "var(--ph-success)"    },
  "warning":       { icon: AlertOctagon, label: "Attention",     tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"    },
  "error":         { icon: AlertOctagon, label: "Error",         tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"     },
  "not-connected": { icon: PlugZap,      label: "Not connected", tint: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"  },
};

export default function ShopifySync() {
  const [store, setStore]     = useState("orvan-perfumes");
  const [token, setToken]     = useState("");
  const [testMode, setTestMode] = useState(true);
  const [autoPush, setAutoPush] = useState(true);

  return (
    <div className="space-y-6" data-testid="cm-sync">
      <Card testId="cm-sync-connection">
        <SectionHeader
          eyebrow="Integration"
          title="Shopify connection"
          action={
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase"
              style={{ letterSpacing: "0.14em", background: "var(--ph-neutral-tint)", color: "var(--ph-ink-muted)", borderRadius: "var(--ph-radius-sm)" }}>
              <PlugZap size={11} /> Not connected
            </span>
          }
        />
        <Field label="Shop domain" hint="Your Shopify admin URL prefix (e.g. orvan-perfumes.myshopify.com)." inline>
          <div style={{ width: 280 }}><TextInput value={store} onChange={setStore} testId="cm-sync-store" /></div>
        </Field>
        <Field label="Admin API token" hint="Read/write scopes: products, inventory, orders, customers, discounts." inline>
          <div style={{ width: 280 }}><TextInput type="password" value={token} onChange={setToken} placeholder="shpat_..." testId="cm-sync-token" /></div>
        </Field>
        <Field label="Test mode" hint="Sync into a scratch collection first — safe for demos." inline>
          <Toggle checked={testMode} onChange={setTestMode} testId="cm-sync-testmode" />
        </Field>
        <Field label="Auto-push changes" hint="Push local changes to Shopify as they happen." inline>
          <Toggle checked={autoPush} onChange={setAutoPush} testId="cm-sync-auto" />
        </Field>
        <div className="pt-4 flex items-center justify-between">
          <div className="text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>
            <KeyRound size={11} className="inline mr-1" />
            Real Shopify integration ships in a later sprint — this screen mocks the wiring.
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" testId="cm-sync-test">Test connection</Button>
            <Button testId="cm-sync-connect">Connect Shopify</Button>
          </div>
        </div>
      </Card>

      <Card padded={false} testId="cm-sync-streams-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Streams" title="Sync status by entity" action={<RefreshCcw size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        </div>
        <ul className="divide-y" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid="cm-sync-streams">
          {SYNC_STREAMS.map((s) => {
            const meta = STATUS_META[s.status];
            const Icon = meta.icon;
            return (
              <li key={s.key} data-testid={`cm-sync-${s.key}`} className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <div className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{s.label}</div>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase"
                      style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      <Icon size={10} /> {meta.label}
                    </span>
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>{s.note}</div>
                </div>
                <div className="text-right hidden md:block">
                  <div className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{s.itemsSynced} synced</div>
                  <div className="text-[11px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>{s.itemsPending} pending · {s.lastSyncRelative}</div>
                </div>
                <Button variant="secondary" testId={`cm-sync-run-${s.key}`}>Sync now</Button>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}
